import { type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Megaphone } from 'lucide-react';
import { useTokenStore } from '../api/use-api.ts';
import { getActiveAnnouncement } from '../api/platform-announcements.ts';

/**
 * Platform-wide maintenance/outage notice, mounted once in App.tsx (alongside
 * OfflineBanner) so it covers every screen alike, signed in or not — the endpoint
 * behind it is public. Polled rather than one-shot: an admin can post one while a
 * user's tab has been open for hours.
 */
export function MaintenanceBanner(): JSX.Element | null {
  const store = useTokenStore();
  const { data } = useQuery({
    queryKey: ['platform-announcement-active'],
    queryFn: () => getActiveAnnouncement(store),
    refetchInterval: 60_000,
    // A failed read (the API itself struggling) says nothing worth showing here —
    // that failure surfaces through the app's own connectivity messaging instead.
    retry: false,
  });

  if (data == null) return null;
  return (
    <div
      role="status"
      data-testid="maintenance-banner"
      className="flex items-center justify-center gap-2 border-b border-watch/30 bg-watch-bg px-4 py-2 text-center text-sm font-medium text-watch"
    >
      <Megaphone className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{data.message}</span>
    </div>
  );
}
